// ==================== DASHBOARD LAYOUT ====================
// Used for: Admin Dashboard, Customer Dashboard (role-based)

import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  CreditCard,
  Settings,
  MessageSquare,
  MessageCircle,
  Send,
  FileText,
  Contact as ContactIcon,
  FolderOpen,
  Menu,
  X,
  ChevronDown,
  LogOut,
  Search,
  PanelLeftClose,
  PanelLeftOpen,
  Bot,
  Target,
  KanbanSquare,
  Mail,
  Calendar,
  BarChart2,
  SlidersHorizontal,
} from 'lucide-react';
import { Logo, LogoIcon, Tooltip } from '../components/ui';
// import { NotificationsDropdown } from '../components/ui';
// import type { Notification } from '../components/ui/NotificationsDropdown';
import { ROUTES } from '../constants';
import { cn } from '../utils/helpers';
import { useAuth } from '../contexts';

// Navigation items based on role
const adminNavItems = [
  {
    label: 'Dashboard',
    href: ROUTES.ADMIN.DASHBOARD,
    icon: LayoutDashboard,
  },
  {
    label: 'Customers',
    href: ROUTES.ADMIN.CUSTOMERS,
    icon: Users,
  },
  {
    label: 'Subscription Plans',
    href: ROUTES.ADMIN.SUBSCRIPTION_PLANS,
    icon: CreditCard,
  },
  {
    label: 'Subscriptions',
    href: ROUTES.ADMIN.SUBSCRIPTIONS,
    icon: CreditCard,
  },
  {
    label: 'Whitepaper Leads',
    href: ROUTES.ADMIN.WHITEPAPER_LEADS,
    icon: FileText,
  },
  {
    label: 'Settings',
    href: ROUTES.ADMIN.SETTINGS,
    icon: Settings,
  },
];

const customerNavItems = [
  {
    label: 'Dashboard',
    href: ROUTES.CUSTOMER.DASHBOARD,
    icon: LayoutDashboard,
  },
  {
    label: 'WhatsApp Inbox',
    href: ROUTES.CUSTOMER.MESSAGES,
    icon: MessageSquare,
  },
  {
    label: 'WA Templates',
    href: ROUTES.CUSTOMER.TEMPLATES,
    icon: FileText,
  },
  {
    label: 'Campaigns',
    href: ROUTES.CUSTOMER.CAMPAIGNS,
    icon: Send,
  },
  {
    label: 'Contacts',
    href: ROUTES.CUSTOMER.CONTACTS,
    icon: ContactIcon,
  },
  {
    label: 'Groups',
    href: ROUTES.CUSTOMER.GROUPS,
    icon: FolderOpen,
  },
  {
    label: 'SMS Inbox',
    href: ROUTES.CUSTOMER.SMS_MESSAGES,
    icon: MessageCircle,
  },
  {
    label: 'SMS Templates',
    href: ROUTES.CUSTOMER.SMS_TEMPLATES,
    icon: FileText,
  },
  {
    label: 'Keyword Bots',
    href: ROUTES.CUSTOMER.KEYWORD_BOTS,
    icon: Bot,
  },
  {
    label: 'Settings',
    href: ROUTES.CUSTOMER.SETTINGS,
    icon: Settings,
  },
];

interface DashboardLayoutProps {
  role?: 'SUPER_ADMIN' | 'CUSTOMER';
}

// Mock notifications data - Hidden temporarily
// const mockNotifications: Notification[] = [
//   {
//     id: '1',
//     type: 'success',
//     title: 'New Customer Registered',
//     message: 'TechCorp Inc. has registered and is waiting for approval.',
//     time: '5 min ago',
//     read: false,
//     icon: 'user',
//   },
//   {
//     id: '2',
//     type: 'info',
//     title: 'Payment Received',
//     message: 'Payment of $299.99 received from GrowthLabs.',
//     time: '1 hour ago',
//     read: false,
//     icon: 'payment',
//   },
//   {
//     id: '3',
//     type: 'warning',
//     title: 'Subscription Expiring',
//     message: 'RetailPro subscription expires in 3 days.',
//     time: '2 hours ago',
//     read: true,
//   },
//   {
//     id: '4',
//     type: 'success',
//     title: 'Plan Upgraded',
//     message: 'DataFlow Systems upgraded to Professional plan.',
//     time: '5 hours ago',
//     read: true,
//     icon: 'payment',
//   },
// ];

const DashboardLayout = ({ role = 'CUSTOMER' }: DashboardLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [leadGenNavOpen, setLeadGenNavOpen] = useState(false);
  // const [notifications, setNotifications] = useState<Notification[]>(mockNotifications);
  const profileButtonRef = useRef<HTMLButtonElement>(null);
  const [profileDropdownPosition, setProfileDropdownPosition] = useState({ top: 0, right: 0 });
  const location = useLocation();
  const { user, logout } = useAuth();

  // Lead Generation nav is gated on the recurring add-on being enabled for this
  // customer's subscription. Disabled/absent add-on => the whole section is hidden.
  const showLeadGenNav =
    role === 'CUSTOMER' && user?.organization?.subscription?.leadGenAddonEnabled === true;

  const navItems = role === 'SUPER_ADMIN' ? adminNavItems : customerNavItems;

  // Update profile dropdown position when opened
  useEffect(() => {
    if (profileDropdownOpen && profileButtonRef.current) {
      const rect = profileButtonRef.current.getBoundingClientRect();
      setProfileDropdownPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right,
      });
    }
  }, [profileDropdownOpen]);
  
  // Use auth context user or fallback
  const displayUser = user || {
    firstName: 'User',
    lastName: '',
    email: 'user@example.com',
    role: role,
  };

  const isActive = (href: string) => location.pathname === href;

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-(--color-dark-navy)">
      {/* Sidebar - Desktop */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-40 h-screen transition-all duration-300 ease-in-out',
          'bg-(--color-midnight-navy)',
          'hidden lg:block',
          sidebarOpen ? 'w-64' : 'w-20'
        )}
      >
        {/* Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-4">
          {sidebarOpen ? (
            <Link to={role === 'SUPER_ADMIN' ? ROUTES.ADMIN.DASHBOARD : ROUTES.CUSTOMER.DASHBOARD}>
              <Logo size="lg" />
            </Link>
          ) : (
            <Link 
              to={role === 'SUPER_ADMIN' ? ROUTES.ADMIN.DASHBOARD : ROUTES.CUSTOMER.DASHBOARD}
              className="mx-auto"
            >
              <LogoIcon size="lg" />
            </Link>
          )}
          
          {sidebarOpen && (
            <button
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg text-(--color-muted-gray) hover:text-(--color-signal-orange) hover:bg-(--color-signal-orange)/10 transition-all duration-200"
              title="Collapse sidebar"
            >
              <PanelLeftClose size={20} />
            </button>
          )}
        </div>

        {/* Toggle Button - Collapsed State */}
        {!sidebarOpen && (
          <div className="px-3 mb-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="w-full p-2.5 rounded-lg text-(--color-muted-gray) hover:text-(--color-signal-orange) hover:bg-(--color-signal-orange)/10 transition-all duration-200 flex items-center justify-center"
              title="Expand sidebar"
            >
              <PanelLeftOpen size={20} />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="px-3 py-4 space-y-1 overflow-y-auto h-[calc(100vh-5rem)]">
          {navItems.map((item) => (
            <Tooltip 
              key={item.href} 
              content={item.label} 
              disabled={sidebarOpen}
              position="right"
            >
              <Link
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                  isActive(item.href)
                    ? 'bg-(--color-orange-glow) text-(--color-signal-orange) border border-[rgba(230,142,55,0.3)]'
                    : 'text-(--color-soft-gray) hover:bg-(--color-signal-orange)/10 hover:text-(--color-signal-orange)',
                  !sidebarOpen && 'justify-center'
                )}
              >
                <item.icon 
                  size={20} 
                  className="shrink-0"
                />
                {sidebarOpen && (
                  <span className="text-sm font-medium">{item.label}</span>
                )}
              </Link>
            </Tooltip>
          ))}

          {showLeadGenNav &&
            (!sidebarOpen ? (
              <Tooltip content="Lead Generation" position="right">
                <Link
                  to={ROUTES.CUSTOMER.LEAD_GEN_DASHBOARD}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200',
                    'justify-center',
                    location.pathname.startsWith('/app/lead-gen')
                      ? 'bg-(--color-orange-glow) text-(--color-signal-orange) border border-[rgba(230,142,55,0.3)]'
                      : 'text-(--color-soft-gray) hover:bg-(--color-signal-orange)/10 hover:text-(--color-signal-orange)'
                  )}
                >
                  <Bot size={20} className="shrink-0" />
                </Link>
              </Tooltip>
            ) : (
              <div className="space-y-1 pt-2 mt-2 border-t border-(--color-border-gray)/50">
                <button
                  type="button"
                  onClick={() => setLeadGenNavOpen((o) => !o)}
                  className={cn(
                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-left',
                    location.pathname.startsWith('/app/lead-gen')
                      ? 'bg-(--color-orange-glow)/50 text-(--color-signal-orange) border border-[rgba(230,142,55,0.3)]'
                      : 'text-(--color-soft-gray) hover:bg-(--color-signal-orange)/10 hover:text-(--color-signal-orange)'
                  )}
                >
                  <Bot size={20} className="shrink-0" />
                  <span className="text-sm font-medium flex-1 min-w-0">Lead Generation</span>
                  <ChevronDown
                    size={16}
                    className={cn('shrink-0 transition-transform', (leadGenNavOpen || location.pathname.startsWith('/app/lead-gen')) && 'rotate-180')}
                  />
                </button>
                {(leadGenNavOpen || location.pathname.startsWith('/app/lead-gen')) && (
                  <div className="space-y-0.5">
                    {[
                      { label: 'Dashboard', href: ROUTES.CUSTOMER.LEAD_GEN_DASHBOARD, icon: LayoutDashboard },
                      { label: 'Lead Discovery', href: ROUTES.CUSTOMER.LEAD_GEN_DISCOVERY, icon: Target },
                      { label: 'Lead Pipeline', href: ROUTES.CUSTOMER.LEAD_GEN_PIPELINE, icon: KanbanSquare },
                      { label: 'Outreach Campaigns', href: ROUTES.CUSTOMER.LEAD_GEN_OUTREACH, icon: Mail },
                      { label: 'Meetings Booked', href: ROUTES.CUSTOMER.LEAD_GEN_MEETINGS, icon: Calendar },
                      { label: 'Performance Reports', href: ROUTES.CUSTOMER.LEAD_GEN_REPORTS, icon: BarChart2 },
                      { label: 'Agent Settings', href: ROUTES.CUSTOMER.LEAD_GEN_SETTINGS, icon: SlidersHorizontal },
                    ].map((item) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          'flex items-center gap-2 pl-10 pr-3 py-2 rounded-lg text-sm transition-all duration-200',
                          isActive(item.href)
                            ? 'text-(--color-signal-orange) bg-(--color-signal-orange)/10'
                            : 'text-(--color-muted-gray) hover:text-(--color-signal-orange) hover:bg-(--color-signal-orange)/10'
                        )}
                      >
                        <item.icon size={14} className="shrink-0" />
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
        </nav>

        {/* Sidebar Footer - User Quick Info (collapsed) */}
        {!sidebarOpen && (
          <div className="absolute bottom-4 left-0 right-0 px-3">
            <Tooltip content={`${displayUser.firstName} ${displayUser.lastName}`} position="right">
              <div className="flex items-center justify-center p-2 rounded-lg bg-white/5">
                <div className="w-8 h-8 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) flex items-center justify-center">
                  <span className="text-xs font-semibold text-white">
                    {displayUser.firstName[0]}{displayUser.lastName?.[0] || ''}
                  </span>
                </div>
              </div>
            </Tooltip>
          </div>
        )}
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Mobile */}
      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen w-72 transition-transform duration-300 ease-in-out lg:hidden',
          'bg-(--color-midnight-navy)',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Mobile Sidebar Header */}
        <div className="flex items-center justify-between h-16 px-4">
          <Logo size="lg" />
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="p-2 rounded-lg text-(--color-muted-gray) hover:text-(--color-signal-orange) hover:bg-(--color-signal-orange)/10 transition-all duration-200"
          >
            <X size={20} />
          </button>
        </div>

        {/* Mobile Navigation */}
        <nav className="px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMobileSidebarOpen(false)}
              className={cn(
                'flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200',
                isActive(item.href)
                  ? 'bg-(--color-orange-glow) text-(--color-signal-orange) border border-[rgba(230,142,55,0.3)]'
                  : 'text-(--color-soft-gray) hover:bg-(--color-signal-orange)/10 hover:text-(--color-signal-orange)'
              )}
            >
              <item.icon size={20} className="shrink-0" />
              <span className="text-sm font-medium">{item.label}</span>
            </Link>
          ))}

          {showLeadGenNav && (
            <div className="space-y-1 pt-2 mt-2 border-t border-(--color-border-gray)/50">
              <button
                type="button"
                onClick={() => setLeadGenNavOpen((o) => !o)}
                className={cn(
                  'w-full flex items-center gap-3 px-3 py-3 rounded-lg transition-all duration-200 text-left',
                  location.pathname.startsWith('/app/lead-gen')
                    ? 'bg-(--color-orange-glow)/50 text-(--color-signal-orange) border border-[rgba(230,142,55,0.3)]'
                    : 'text-(--color-soft-gray) hover:bg-(--color-signal-orange)/10 hover:text-(--color-signal-orange)'
                )}
              >
                <Bot size={20} className="shrink-0" />
                <span className="text-sm font-medium flex-1 min-w-0">Lead Generation</span>
                <ChevronDown
                  size={16}
                  className={cn('shrink-0 transition-transform', (leadGenNavOpen || location.pathname.startsWith('/app/lead-gen')) && 'rotate-180')}
                />
              </button>
              {(leadGenNavOpen || location.pathname.startsWith('/app/lead-gen')) && (
                <div className="space-y-0.5">
                  {[
                    { label: 'Dashboard', href: ROUTES.CUSTOMER.LEAD_GEN_DASHBOARD, icon: LayoutDashboard },
                    { label: 'Lead Discovery', href: ROUTES.CUSTOMER.LEAD_GEN_DISCOVERY, icon: Target },
                    { label: 'Lead Pipeline', href: ROUTES.CUSTOMER.LEAD_GEN_PIPELINE, icon: KanbanSquare },
                    { label: 'Outreach Campaigns', href: ROUTES.CUSTOMER.LEAD_GEN_OUTREACH, icon: Mail },
                    { label: 'Meetings Booked', href: ROUTES.CUSTOMER.LEAD_GEN_MEETINGS, icon: Calendar },
                    { label: 'Performance Reports', href: ROUTES.CUSTOMER.LEAD_GEN_REPORTS, icon: BarChart2 },
                    { label: 'Agent Settings', href: ROUTES.CUSTOMER.LEAD_GEN_SETTINGS, icon: SlidersHorizontal },
                  ].map((item) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMobileSidebarOpen(false)}
                      className={cn(
                        'flex items-center gap-2 pl-10 pr-3 py-2 rounded-lg text-sm transition-all duration-200',
                        isActive(item.href)
                          ? 'text-(--color-signal-orange) bg-(--color-signal-orange)/10'
                          : 'text-(--color-muted-gray) hover:text-(--color-signal-orange) hover:bg-(--color-signal-orange)/10'
                      )}
                    >
                      <item.icon size={14} className="shrink-0" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Mobile User Info */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-(--color-midnight-navy)">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5">
            <div className="w-10 h-10 rounded-full bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) flex items-center justify-center">
              <span className="text-sm font-semibold text-white">
                {displayUser.firstName[0]}{displayUser.lastName?.[0] || ''}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">
                {displayUser.firstName} {displayUser.lastName}
              </p>
              <p className="text-xs text-(--color-muted-gray) truncate">
                {displayUser.email}
              </p>
            </div>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg text-(--color-error) hover:bg-(--color-error)/10 transition-colors"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div
        className={cn(
          'transition-all duration-300 ease-in-out',
          sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'
        )}
      >
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-(--color-midnight-navy)/95 backdrop-blur-md">
          <div className="flex items-center justify-between h-full px-4 lg:px-6">
            {/* Left Side */}
            <div className="flex items-center gap-4">
              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileSidebarOpen(true)}
                className="lg:hidden p-2 rounded-lg text-(--color-muted-gray) hover:text-(--color-signal-orange) hover:bg-(--color-signal-orange)/10 transition-all duration-200"
              >
                <Menu size={20} />
              </button>

              {/* Search Bar */}
              <div className="hidden md:flex items-center">
                <div className="relative">
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-(--color-muted-gray)"
                  />
                  <input
                    type="text"
                    placeholder="Search..."
                    className="w-64 pl-10 pr-4 py-2.5 text-sm bg-(--color-navy-light)/50 border border-(--color-border-gray)/50 rounded-xl text-white placeholder:text-(--color-muted-gray) focus:border-(--color-signal-orange)/50 focus:bg-(--color-navy-light) focus:outline-none transition-all duration-200"
                  />
                </div>
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center gap-2">
              {/* Notifications - Hidden temporarily */}
              {/* <NotificationsDropdown
                notifications={notifications}
                onMarkAsRead={(id) => {
                  setNotifications(notifications.map(n => 
                    n.id === id ? { ...n, read: true } : n
                  ));
                }}
                onMarkAllAsRead={() => {
                  setNotifications(notifications.map(n => ({ ...n, read: true })));
                }}
                onClearAll={() => setNotifications([])}
                onViewAll={() => {
                  // Navigate to notifications page if exists
                  console.log('View all notifications');
                }}
              /> */}

              {/* Profile Dropdown */}
              <div className="relative">
                <button
                  ref={profileButtonRef}
                  onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                  className={cn(
                    'flex items-center gap-3 p-2 rounded-xl transition-all duration-200',
                    profileDropdownOpen 
                      ? 'bg-(--color-signal-orange)/10' 
                      : 'hover:bg-white/5'
                  )}
                >
                  <div className="w-9 h-9 rounded-xl bg-linear-to-br from-(--color-signal-orange) to-(--color-warm-amber) flex items-center justify-center shadow-lg shadow-(--color-signal-orange)/20">
                    <span className="text-sm font-bold text-white">
                      {displayUser.firstName[0]}{displayUser.lastName?.[0] || ''}
                    </span>
                  </div>
                  <div className="hidden md:block text-left">
                    <p className="text-sm font-medium text-white">
                      {displayUser.firstName} {displayUser.lastName}
                    </p>
                    <p className="text-xs text-(--color-muted-gray)">
                      {displayUser.role === 'SUPER_ADMIN' ? 'Super Admin' : 'Customer'}
                    </p>
                  </div>
                  <ChevronDown 
                    size={16} 
                    className={cn(
                      'text-(--color-muted-gray) transition-transform duration-200',
                      profileDropdownOpen && 'rotate-180'
                    )} 
                  />
                </button>

                {/* Dropdown Menu - rendered via portal to escape stacking context */}
                {profileDropdownOpen && createPortal(
                  <>
                    {/* Backdrop - covers entire screen to capture outside clicks */}
                    <div
                      className="fixed inset-0 z-[100]"
                      onClick={() => setProfileDropdownOpen(false)}
                    />
                    <div 
                      className="fixed w-64 py-2 bg-(--color-charcoal-black) rounded-xl shadow-2xl z-[101] overflow-hidden"
                      style={{ top: profileDropdownPosition.top, right: profileDropdownPosition.right }}
                    >
                      <div className="px-4 py-3 bg-white/5">
                        <p className="text-sm font-semibold text-white">
                          {displayUser.firstName} {displayUser.lastName}
                        </p>
                        <p className="text-xs text-(--color-muted-gray) mt-0.5">{displayUser.email}</p>
                      </div>
                      
                      <div className="py-1">
                        <Link
                          to={role === 'SUPER_ADMIN' ? ROUTES.ADMIN.SETTINGS : ROUTES.CUSTOMER.SETTINGS}
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-(--color-soft-gray) hover:bg-white/5 hover:text-white transition-colors"
                          onClick={() => setProfileDropdownOpen(false)}
                        >
                          <Settings size={16} />
                          Settings
                        </Link>
                      </div>
                      
                      <div className="border-t border-(--color-border-gray)/50 pt-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-(--color-error) hover:bg-(--color-error)/10 transition-colors"
                        >
                          <LogOut size={16} />
                          Sign out
                        </button>
                      </div>
                    </div>
                  </>,
                  document.body
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export { DashboardLayout };
